import { StatoWO, PrioritaWO, TipologiaWO } from '../types'

/* ── Shared badge primitive ────────────────────────────────── */
interface BS { bg: string; color: string; dot: string }

function Pill({ label, s, showDot = true }: { label: string; s: BS; showDot?: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: 99,
      fontSize: 11, fontWeight: 500, lineHeight: 1.6, whiteSpace: 'nowrap',
      background: s.bg, color: s.color,
      border: `1px solid ${s.dot}28`,
      fontFamily: "'Outfit', system-ui, sans-serif",
    }}>
      {showDot && (
        <span style={{
          width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
          background: s.dot, boxShadow: `0 0 5px ${s.dot}90`,
        }} />
      )}
      {label}
    </span>
  )
}

/* ── State badge ───────────────────────────────────────────── */
const STATO: Record<StatoWO, { label: string; s: BS }> = {
  ricevuto:           { label: 'Ricevuto',       s: { bg: 'rgba(100,116,139,.12)', color: '#94a3b8', dot: '#64748b' } },
  in_verifica:        { label: 'In Verifica',    s: { bg: 'rgba(59,130,246,.12)',  color: '#60a5fa', dot: '#3b82f6' } },
  pianificato:        { label: 'Pianificato',    s: { bg: 'rgba(99,102,241,.12)',  color: '#818cf8', dot: '#6366f1' } },
  assegnato:          { label: 'Assegnato',      s: { bg: 'rgba(139,92,246,.12)',  color: '#a78bfa', dot: '#8b5cf6' } },
  confermato_tecnico: { label: 'Confermato',     s: { bg: 'rgba(168,85,247,.12)', color: '#c084fc', dot: '#a855f7' } },
  in_lavorazione:     { label: 'In Lavorazione', s: { bg: 'rgba(245,158,11,.12)',  color: '#fbbf24', dot: '#f59e0b' } },
  completato:         { label: 'Completato',     s: { bg: 'rgba(34,197,94,.12)',   color: '#4ade80', dot: '#22c55e' } },
  in_validazione:     { label: 'In Validazione', s: { bg: 'rgba(20,184,166,.12)',  color: '#2dd4bf', dot: '#14b8a6' } },
  validato:           { label: 'Validato',       s: { bg: 'rgba(16,185,129,.12)',  color: '#34d399', dot: '#10b981' } },
  fatturabile:        { label: 'Fatturabile',    s: { bg: 'rgba(6,182,212,.12)',   color: '#22d3ee', dot: '#06b6d4' } },
  fatturato:          { label: 'Fatturato',      s: { bg: 'rgba(59,130,246,.15)',  color: '#93c5fd', dot: '#60a5fa' } },
  chiuso:             { label: 'Chiuso',         s: { bg: 'rgba(100,116,139,.08)', color: '#64748b', dot: '#475569' } },
  annullato:          { label: 'Annullato',      s: { bg: 'rgba(239,68,68,.12)',   color: '#f87171', dot: '#ef4444' } },
}

export function StatoBadge({ stato }: { stato: StatoWO }) {
  const c = STATO[stato] ?? { label: stato, s: { bg: 'rgba(100,116,139,.12)', color: '#94a3b8', dot: '#64748b' } }
  return <Pill label={c.label} s={c.s} />
}

/* ── Priority badge ────────────────────────────────────────── */
const PRIORITA: Record<PrioritaWO, { label: string; s: BS }> = {
  critica: { label: 'Critica', s: { bg: 'rgba(239,68,68,.15)',   color: '#f87171', dot: '#ef4444' } },
  urgente: { label: 'Urgente', s: { bg: 'rgba(249,115,22,.15)',  color: '#fb923c', dot: '#f97316' } },
  alta:    { label: 'Alta',    s: { bg: 'rgba(234,179,8,.12)',   color: '#fcd34d', dot: '#eab308' } },
  normale: { label: 'Normale', s: { bg: 'rgba(34,197,94,.10)',   color: '#4ade80', dot: '#22c55e' } },
}

export function PrioritaBadge({ priorita }: { priorita: PrioritaWO }) {
  const c = PRIORITA[priorita] ?? { label: priorita, s: { bg: 'rgba(100,116,139,.12)', color: '#94a3b8', dot: '#64748b' } }
  return <Pill label={c.label} s={c.s} />
}

/* ── Type badge ────────────────────────────────────────────── */
export function TipologiaBadge({ tipologia }: { tipologia: TipologiaWO }) {
  const label = tipologia.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  return (
    <Pill
      label={label}
      s={{ bg: 'rgba(56,189,248,.10)', color: '#38bdf8', dot: '#0ea5e9' }}
      showDot={false}
    />
  )
}

/* ── SLA indicator ─────────────────────────────────────────── */
export function SLAIndicator({ sla_chiusura, sla_violato }: { sla_chiusura?: string | Date | null; sla_violato?: boolean }) {
  if (!sla_chiusura) return null
  const s = new Date(sla_chiusura)
  const now = new Date()
  const diff = s.getTime() - now.getTime()
  const hours = diff / (1000 * 60 * 60)
  if (sla_violato) {
    return (
      <span style={{ fontSize: 10, fontWeight: 600, color: '#ef4444', background: '#ef444415', padding: '1px 6px', borderRadius: 99 }}>
        SLA Violato
      </span>
    )
  }
  const color = hours < 0 ? '#ef4444' : hours < 24 ? '#f97316' : hours < 72 ? '#eab308' : '#22c55e'
  const label = hours < 0 ? 'Scaduto' : hours < 24 ? '< 24h' : hours < 72 ? '< 72h' : 'OK'
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color, background: color + '15', padding: '1px 6px', borderRadius: 99 }}>
      SLA {label}
    </span>
  )
}
